import { AnalyteDTO } from "../DTOs/AnalyteDTO";
import { BucketFactorDTO } from "../DTOs/BucketFactorDTO";
import { BucketTypeDTO } from "../DTOs/BucketTypeDTO";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { DensityDTO } from "../DTOs/DensityDTO";
import { ZoneDTO } from "../DTOs/ZoneDTO";
import { MaterialTypeClassDTO } from "../DTOs/MaterialTypeClassDTO";
import { MaterialTypeDTO } from "../DTOs/MaterialTypeDTO";
import { PitStageDTO } from "../DTOs/PitStageDTO";
import { DataTableFilteredRequest } from "../components/DataTable/TableHook";
import { ShiftDTO } from "../DTOs/ShiftDTO";
import { ajaxController, formatDatesForDateOnlyModel } from "./controller/AjaxController";
import { LocationDTO, LocationGeofenceDTO } from "../DTOs/Production/LocationDTO";
import { HaulRoadDTO } from "../DTOs/Production/HaulRoadDTO";
import { MapFeatureDTO } from "../DTOs/Production/MapFeatureDTO";
import { Option } from "../DTOs/Common/OptionResponse";
import { PlantBucketDTO } from "../DTOs/PlantBucketDTO";
import { PlantDTO } from "../DTOs/Plants/PlantDTO";
import { PlantLookupDTO } from "../DTOs/Plants/PlantLookupDTO";
import AddNewPlantRequestDTO from "../DTOs/Plants/AddNewPlantRequestDTO";

export class ReferencesAjax {
  private _ajaxController = ajaxController;
  private _referencesDataEndpoint = "/References";

  async getAnalytes(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<AnalyteDTO>>(
      `${this._referencesDataEndpoint}/analytes`,
      filters
    );
  }

  async updateAnalyte(data: AnalyteDTO) {
    const response = await this._ajaxController.put<AnalyteDTO>(
      `${this._referencesDataEndpoint}/analytes/${data.id}`,
      data
    );
    return response;
  }

  async addAnalyte(data: AnalyteDTO) {
    const response = await this._ajaxController.post<AnalyteDTO, AnalyteDTO>(
      `${this._referencesDataEndpoint}/analytes`,
      data
    );
    return response;
  }

  async deleteAnalyte(id: number) {
    const response = await this._ajaxController.delete(
      `${this._referencesDataEndpoint}/analytes/${id}`
    );
    return response;
  }

  async getBucketFactor(id: number) {
    return this._ajaxController.get<BucketFactorDTO>(
      `${this._referencesDataEndpoint}/bucket-factors/${id}`,
      {}
    );
  }

  async getBucketFactors(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<BucketFactorDTO>>(
      `${this._referencesDataEndpoint}/bucket-factors`,
      filters,
      undefined,
      formatDatesForDateOnlyModel
    );
  }

  async updateBucketFactor(data: BucketFactorDTO) {
    return this._ajaxController.put<BucketFactorDTO>(
      `${this._referencesDataEndpoint}/bucket-factors/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async addBucketFactor(data: BucketFactorDTO) {
    return this._ajaxController.post<BucketFactorDTO, BucketFactorDTO>(
      `${this._referencesDataEndpoint}/bucket-factors`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteBucketFactor(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/bucket-factors/${id}`
    );
  }

  async getBucketType(id: number) {
    return this._ajaxController.get<BucketTypeDTO>(
      `${this._referencesDataEndpoint}/bucket-types/${id}`,
      {}
    );
  }

  async getBucketTypes(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<BucketTypeDTO>>(
      `${this._referencesDataEndpoint}/bucket-types`,
      filters
    );
  }

  async updateBucketType(data: BucketTypeDTO) {
    return this._ajaxController.put<BucketTypeDTO>(
      `${this._referencesDataEndpoint}/bucket-types/${data.id}`,
      data
    );
  }

  async addBucketType(data: BucketTypeDTO) {
    return this._ajaxController.post<BucketTypeDTO, BucketTypeDTO>(
      `${this._referencesDataEndpoint}/bucket-types`,
      data
    );
  }

  async deleteBucketType(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/bucket-types/${id}`
    );
  }

  async getDensity(id: number) {
    return this._ajaxController.get<DensityDTO>(
      `${this._referencesDataEndpoint}/densities/${id}`,
      {}
    );
  }

  async getDensities(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<DensityDTO>>(
      `${this._referencesDataEndpoint}/densities`,
      filters
    );
  }

  async updateDensity(data: DensityDTO) {
    return this._ajaxController.put<DensityDTO>(
      `${this._referencesDataEndpoint}/densities/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async addDensity(data: DensityDTO) {
    return this._ajaxController.post<DensityDTO, DensityDTO>(
      `${this._referencesDataEndpoint}/densities`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteDensity(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/densities/${id}`
    );
  }

  async getZone(id: number) {
    return this._ajaxController.get<ZoneDTO>(
      `${this._referencesDataEndpoint}/zones/${id}`,
      {}
    );
  }

  async getZones(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<ZoneDTO>>(
      `${this._referencesDataEndpoint}/zones`,
      filters
    );
  }

  async updateZone(data: ZoneDTO) {
    return this._ajaxController.put<ZoneDTO>(
      `${this._referencesDataEndpoint}/zones/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async addZone(data: ZoneDTO) {
    return this._ajaxController.post<ZoneDTO, ZoneDTO>(
      `${this._referencesDataEndpoint}/zones`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteZone(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/zones/${id}`
    );
  }

  async getMaterialType(id: number) {
    return this._ajaxController.get<MaterialTypeDTO>(
      `${this._referencesDataEndpoint}/material-types/${id}`,
      {}
    );
  }

  async getMaterialTypes(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<MaterialTypeDTO>>(
      `${this._referencesDataEndpoint}/material-types`,
      filters
    );
  }

  async updateMaterialType(data: MaterialTypeDTO) {
    return this._ajaxController.put<MaterialTypeDTO>(
      `${this._referencesDataEndpoint}/material-types/${data.id}`,
      data
    );
  }

  async addMaterialType(data: MaterialTypeDTO) {
    return this._ajaxController.post<MaterialTypeDTO, MaterialTypeDTO>(
      `${this._referencesDataEndpoint}/material-types`,
      data
    );
  }

  async deleteMaterialType(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/material-types/${id}`
    );
  }

  async getMaterialTypeClass(id: number) {
    return this._ajaxController.get<MaterialTypeClassDTO>(
      `${this._referencesDataEndpoint}/material-type-classes/${id}`,
      {}
    );
  }

  async getMaterialTypeClasses(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<MaterialTypeClassDTO>>(
      `${this._referencesDataEndpoint}/material-type-classes`,
      filters
    );
  }

  async updateMaterialTypeClass(data: MaterialTypeClassDTO) {
    return this._ajaxController.put<MaterialTypeClassDTO>(
      `${this._referencesDataEndpoint}/material-type-classes/${data.id}`,
      data
    );
  }

  async addMaterialTypeClass(data: MaterialTypeClassDTO) {
    return this._ajaxController.post<MaterialTypeClassDTO, MaterialTypeClassDTO>(
      `${this._referencesDataEndpoint}/material-type-classes`,
      data
    );
  }

  async deleteMaterialTypeClass(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/material-type-classes/${id}`
    );
  }

  async getPits(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._referencesDataEndpoint}/pits`,
      filters
    );
  }

  async getPitStages(filters: DataTableFilteredRequest) {
    const formattedFilters = formatDatesForDateOnlyModel(filters);
    return this._ajaxController.get<PagedResult<PitStageDTO>>(
      `${this._referencesDataEndpoint}/pit-stages`,
      formattedFilters
    );
  }

  async updatePitStage(data: PitStageDTO) {
    return this._ajaxController.put<PitStageDTO>(
      `${this._referencesDataEndpoint}/pit-stages/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async addPitStage(data: PitStageDTO) {
    return this._ajaxController.post<PitStageDTO, PitStageDTO>(
      `${this._referencesDataEndpoint}/pit-stages`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deletePitStage(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/pit-stages/${id}`
    );
  }

  async getPlant(id: number) {
    return this._ajaxController.get<PlantLookupDTO>(
      `${this._referencesDataEndpoint}/plants/${id}`,
      {}
    );
  }

  async getPlants(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<PlantLookupDTO>>(
      `${this._referencesDataEndpoint}/plants`,
      filters
    );
  }

  async getAdministeredPlant(id: number) {
    return this._ajaxController.get<PlantDTO>(
      `${this._referencesDataEndpoint}/plants/administered/${id}`,
      {}
    );
  }

  async getAdministeredPlants(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<PlantDTO>>(
      `${this._referencesDataEndpoint}/plants/administered`,
      filters
    );
  }

  async updatePlant(data: PlantDTO) {
    return this._ajaxController.put<PlantDTO>(
      `${this._referencesDataEndpoint}/plants/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async addPlant(data: AddNewPlantRequestDTO) {
    return this._ajaxController.post<AddNewPlantRequestDTO, PlantDTO>(
      `${this._referencesDataEndpoint}/plants`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deletePlant(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/plants/${id}`
    );
  }

  async getPlantBucket(id: number) {
    return this._ajaxController.get<PlantBucketDTO>(
      `${this._referencesDataEndpoint}/plant-buckets/${id}`,
      {}
    );
  }

  async getPlantBuckets(filters: DataTableFilteredRequest) {
    const formattedFilters = formatDatesForDateOnlyModel(filters);
    return this._ajaxController.get<PagedResult<PlantBucketDTO>>(
      `${this._referencesDataEndpoint}/plant-buckets`,
      formattedFilters
    );
  }

  async updatePlantBucket(data: PlantBucketDTO) {
    return this._ajaxController.put<PlantBucketDTO>(
      `${this._referencesDataEndpoint}/plant-buckets/${data.plantBucketId}`,
      data
    );
  }

  async addPlantBucket(data: PlantBucketDTO) {
    return this._ajaxController.post<PlantBucketDTO, PlantBucketDTO>(
      `${this._referencesDataEndpoint}/plant-buckets`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deletePlantBucket(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/plant-buckets/${id}`
    );
  }

  async getLatestShift() {
    return this._ajaxController.get<ShiftDTO>(
      `${this._referencesDataEndpoint}/latest`,
      {}
    );
  }

  async getHaulRoads() {
    return this._ajaxController.get<PagedResult<HaulRoadDTO>>(
      `${this._referencesDataEndpoint}/haul-roads`,
      {}
    );
  }

  async getMapFeatures() {
    return this._ajaxController.get<PagedResult<MapFeatureDTO>>(
      `${this._referencesDataEndpoint}/map-features`,
      {}
    );
  }

  async getLocations(params: { request?: DataTableFilteredRequest; locationTypes?: number[]; includeInactive?: boolean }) {
    const query = {
      locationTypes: params.locationTypes,
      includeInactive: params.includeInactive ?? false,
      request: { pageNumber: 1, pageSize: 1000, ...(params.request ?? {}) }
    };
    return this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._referencesDataEndpoint}/locations`,
      query
    );
  }

  async getJobTypes() {
    return this._ajaxController.get<Option<number>[]>(
      `${this._referencesDataEndpoint}/job-types`,
      {}
    );
  }
  async addLocation(data: LocationDTO) {
    return this._ajaxController.post<LocationDTO, LocationDTO>(
      `${this._referencesDataEndpoint}/locations`,
      data
    );
  }

  async updateLocation(data: LocationDTO) {
    return this._ajaxController.put<LocationDTO>(
      `${this._referencesDataEndpoint}/locations/${data.id}`,
      data
    );
  }

  // Note: separate from updateLocation on purpose - see LocationGeofenceDto server-side. The location
  // PUT does not carry the geofence, so it cannot destroy one it was not told about.
  async updateLocationGeofence(id: number, data: LocationGeofenceDTO) {
    return this._ajaxController.put<LocationGeofenceDTO, LocationDTO>(
      `${this._referencesDataEndpoint}/locations/${id}/geofence`,
      data
    );
  }

  async deleteLocation(id: number) {
    return this._ajaxController.delete(
      `${this._referencesDataEndpoint}/locations/${id}`
    );
  }

  async getLocationTypeOptions() {
    return this._ajaxController.get<PagedResult<Option>>(
      `${this._referencesDataEndpoint}/location-types`,
      {}
    );
  }
}

export const referencesAjax = new ReferencesAjax();
